<template>
  <div>
    <app-spinner v-if="!is_page_loaded" />
    <app-page-container v-else>
      <section class="schedule-problems">
        <app-filters
          v-model="selected_problem_type"
          :filters="problemFilters"
          name="scheduleProblemType"
          class="schedule-problems__filters"
          @change="fetchScheduleProblems"
        />
        <app-info-text-panel class="schedule-problems__info">
          Showing <strong>{{ schedule_problems.summary.filtered_total }}</strong> of
          <strong>{{ schedule_problems.summary.total }}</strong> schedule conflicts:
          {{ schedule_problems.summary.booking_overlap }} booking overlaps,
          {{ schedule_problems.summary.maintenance_conflict }} maintenance conflicts and
          {{ schedule_problems.summary.guide_leave_conflict }} assigned guide leave conflicts.
        </app-info-text-panel>
        <div v-if="schedule_problems.vehicles.length" class="schedule-problems__vehicles">
          <article v-for="group in schedule_problems.vehicles" :key="group.vehicle.id" class="schedule-problems__vehicle">
            <header>
              <div>
                <strong>{{ group.vehicle.plate_number || group.vehicle.name }}</strong>
                <span>{{ group.vehicle.type === 'own' ? 'Altezza car' : 'Rental car' }}</span>
              </div>
              <div v-if="group.vehicle.current_driver" class="schedule-problems__driver">
                <span>Current driver</span>
                <strong>{{ group.vehicle.current_driver.display_name || group.vehicle.current_driver.full_name }}</strong>
              </div>
              <b>{{ group.conflicts.length }} problem{{ group.conflicts.length === 1 ? '' : 's' }}</b>
            </header>
            <div class="schedule-problems__items">
              <div v-for="conflict in group.conflicts" :key="conflict.id" class="schedule-problems__item" :class="`schedule-problems__item--${conflict.kind}`">
                <div class="schedule-problems__item-heading">
                  <span class="status-tag status-tag--small" :style="{ backgroundColor: problemColor(conflict.kind) }">{{ problemLabel(conflict.kind) }}</span>
                  <div class="schedule-problems__dates">
                    <span><small>From</small><strong>{{ dateFormat(conflict.from_date) }}</strong></span>
                    <span><small>To</small><strong>{{ dateFormat(conflict.to_date) }}</strong></span>
                  </div>
                </div>
                <div v-if="conflict.availability" class="schedule-problems__availability">
                  <strong>{{ conflict.availability.title }}</strong>
                  <span v-if="conflict.availability.employee">{{ conflict.availability.employee.name }}</span>
                  <span v-if="conflict.availability.description">{{ conflict.availability.description }}</span>
                </div>
                <div class="schedule-problems__bookings">
                  <span>Affected booking{{ conflict.bookings.length === 1 ? '' : 's' }}</span>
                  <router-link
                    v-for="booking in conflict.bookings"
                    :key="booking.id"
                    :to="{ name: 'reservation-safari-detail', params: { id: booking.reservation_id } }"
                    class="schedule-problems__booking"
                  >
                    <strong class="table-default__link-border">{{ booking.title }}</strong>
                    <span><small>From</small><strong>{{ dateFormat(booking.from_date) }}</strong></span>
                    <span><small>To</small><strong>{{ dateFormat(booking.to_date) }}</strong></span>
                  </router-link>
                </div>
              </div>
            </div>
          </article>
        </div>
        <app-no-results v-else text="There are no schedule conflicts in the selected period" />
      </section>
      <template v-if="table_deactivated_car.items.length > 0">
        <app-cells :indent="false" margin-bottom="15px" margin-top="30px">
          <h2 class="title">Deactivated Rental Cars</h2>
        </app-cells>
        <v-client-table
          :data="table_deactivated_car.items"
          :columns="table_deactivated_car.columns"
          :options="table_deactivated_car.options"
          class="table-default"
        >
          <template #start="{ row }">{{ dateFormat(row.reservation_safari_car.tour_start) }}</template>
          <template #finish="{ row }">{{ dateFormat(row.reservation_safari_car.tour_end) }}</template>
          <template #group="{ row }">
            <router-link
              :to="{ name: 'reservation-safari-detail', params: { id: row.reservation_safari_car.id } }"
              class="table-default__link-border table-default__link-border--center"
            >
              {{ row.reservation_safari_car.lead.name }}
            </router-link>
          </template>
          <template #car="{ row }">
            {{ row.car.plate_number }}
          </template>
        </v-client-table>
      </template>
      <app-cells :indent="false" margin-bottom="15px" margin-top="30px">
        <h2 class="title">Missing Safari Group</h2>
      </app-cells>
      <template v-if="missing_group_reservations.length > 0">
        <AppAccSafari
          v-for="(safari, safari_index) in missing_group_reservations"
          :key="`missing_group_reservations_${safari.id}`"
          :reservation="safari"
          :order-number="safari_index + 1"
        />
        <app-cells :position="pagination_missing_group.pages > 1 ? 'between' : 'end'">
          <paginate
            :clickHandler="onClickMissingGroupPagination"
            v-if="pagination_missing_group.pages > 1"
            v-model="pagination_missing_group.page"
            :page-count="pagination_missing_group.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
          <app-table-records
            :all="pagination_missing_group.count"
            :count="missing_group_reservations.length"
            :page="pagination_missing_group.page"
          />
        </app-cells>
      </template>
      <app-no-results v-else text="There’s no reservations yet" />
      <template v-if="cars_mismatch_reservations.length > 0">
        <app-cells :indent="false" margin-bottom="15px" margin-top="40px">
          <h2 class="title">Cars Mismatch</h2>
        </app-cells>
        <AppAccSafari
          v-for="(safari, safari_index) in cars_mismatch_reservations"
          :key="`cars_mismatch_reservations_${safari.id}`"
          :reservation="safari"
          :order-number="safari_index + 1"
        />
        <app-cells :position="pagination_cars_mismatch.pages > 1 ? 'between' : 'end'">
          <paginate
            :clickHandler="onClickCarsMismatchPagination"
            v-if="pagination_cars_mismatch.pages > 1"
            v-model="pagination_cars_mismatch.page"
            :page-count="pagination_cars_mismatch.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
          <app-table-records
            :all="pagination_cars_mismatch.count"
            :count="cars_mismatch_reservations.length"
            :page="pagination_cars_mismatch.page"
          />
        </app-cells>
      </template>
      <template v-if="data_mismatch_reservations.length > 0">
        <app-cells :indent="false" margin-bottom="15px" margin-top="40px">
          <h2 class="title">Data Mismatch</h2>
        </app-cells>
        <AppAccSafari
          v-for="(safari, safari_index) in data_mismatch_reservations"
          :key="`cars_mismatch_reservations_${safari.id}`"
          :reservation="safari"
          :order-number="safari_index + 1"
        />
        <app-cells :position="pagination_data_mismatch.pages > 1 ? 'between' : 'end'">
          <paginate
            :clickHandler="onClickDataMismatchPagination"
            v-if="pagination_data_mismatch.pages > 1"
            v-model="pagination_data_mismatch.page"
            :page-count="pagination_data_mismatch.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
          <app-table-records
            :all="pagination_data_mismatch.count"
            :count="data_mismatch_reservations.length"
            :page="pagination_data_mismatch.page"
          />
        </app-cells>
      </template>
    </app-page-container>
  </div>
</template>

<script>
import {getReservationsSafari, getReservationsSafariRentalCars, getReservationsSafariScheduleProblems} from '@/http'
import { addYears, format, parseISO } from 'date-fns'
import AppAccSafari from '@/components/AppAccSafari.vue'
import AppInfoTextPanel from '@/components/AppInfoTextPanel.vue'

export default {
  name: 'SafariScheduleProblems',
  components: { AppAccSafari, AppInfoTextPanel },
  data() {
    return {
      is_page_loaded: false,
      is_schedule_problems_loading: false,
      selected_problem_type: 'all',
      schedule_problems: {
        summary: { booking_overlap: 0, maintenance_conflict: 0, guide_leave_conflict: 0, total: 0, filtered_total: 0 },
        vehicles: [],
      },
      table_deactivated_car: {
        items: [],
        columns: ['start', 'finish', 'group', 'car'],
        options: {
          headings: {
            start: 'Start',
            finish: 'Finish',
            group: 'Group',
            car: 'Car',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      missing_group_reservations: [],
      cars_mismatch_reservations: [],
      data_mismatch_reservations: [],
      pagination_missing_group: {
        pages: 1,
        page: 1,
        count: 1,
      },
      pagination_cars_mismatch: {
        pages: 1,
        page: 1,
        count: 1,
      },
      pagination_data_mismatch: {
        pages: 1,
        page: 1,
        count: 1,
      },
    }
  },
  created() {
    Promise.all([
      getReservationsSafariScheduleProblems({
        from_date: format(new Date(), 'yyyy-MM-dd'),
        to_date: format(addYears(new Date(), 1), 'yyyy-MM-dd'),
        problem_type: this.selected_problem_type,
      }),
      getReservationsSafariRentalCars({ problem: 'deactivated_car' }),
      getReservationsSafari({
        problems: 'safari_group',
        page: this.pagination_missing_group.page,
        page_size: 20,
      }),
      getReservationsSafari({
        problems: 'cars',
        page: this.pagination_cars_mismatch.page,
        page_size: 20,
      }),
      getReservationsSafari({
        problems: 'data',
        page: this.pagination_data_mismatch.page,
        page_size: 20,
      })
    ])
      .then(res => {
        this.schedule_problems = res[0].data
        this.table_deactivated_car.items = res[1].data
        this.missing_group_reservations = res[2].data.results
        this.pagination_missing_group.pages = res[2].data.pages
        this.pagination_missing_group.count = res[2].data.count
        this.cars_mismatch_reservations = res[3].data.results
        this.pagination_cars_mismatch.pages = res[3].data.pages
        this.pagination_cars_mismatch.count = res[3].data.count
        this.data_mismatch_reservations = res[4].data.results
        this.pagination_data_mismatch.pages = res[4].data.pages
        this.pagination_data_mismatch.count = res[4].data.count
        this.is_page_loaded = true
      })
  },
  computed: {
    problemFilters() {
      const summary = this.schedule_problems.summary
      return [
        { value: 'all', label: `All (${summary.total})` },
        { value: 'booking_overlap', label: `Booking overlaps (${summary.booking_overlap})` },
        { value: 'maintenance_conflict', label: `Maintenance (${summary.maintenance_conflict})` },
        { value: 'guide_leave_conflict', label: `Assigned guide leave (${summary.guide_leave_conflict})` },
      ]
    },
  },
  methods: {
    fetchScheduleProblems(problemType) {
      this.selected_problem_type = problemType
      this.is_schedule_problems_loading = true
      getReservationsSafariScheduleProblems({
        from_date: format(new Date(), 'yyyy-MM-dd'),
        to_date: format(addYears(new Date(), 1), 'yyyy-MM-dd'),
        problem_type: problemType,
      })
        .then(res => {
          if (this.selected_problem_type === problemType) this.schedule_problems = res.data
        })
        .finally(() => {
          if (this.selected_problem_type === problemType) this.is_schedule_problems_loading = false
        })
    },
    fetchMissingGroupReservations() {
      getReservationsSafari({
        problems: 'safari_group',
        page: this.pagination_missing_group.page,
        page_size: 20,
      })
        .then(res => {
          this.missing_group_reservations = res.data.results
          this.pagination_missing_group.pages = res.data.pages
          this.pagination_missing_group.count = res.data.count
        })
    },
    fetchCarsMismatchReservations() {
      getReservationsSafari({
        problems: 'cars',
        page: this.pagination_cars_mismatch.page,
        page_size: 20,
      })
        .then(res => {
          this.cars_mismatch_reservations = res.data.results
          this.pagination_cars_mismatch.pages = res.data.pages
          this.pagination_cars_mismatch.count = res.data.count
        })
    },
    fetchDataMismatchReservations() {
      getReservationsSafari({
        problems: 'data',
        page: this.pagination_data_mismatch.page,
        page_size: 20,
      })
        .then(res => {
          this.data_mismatch_reservations = res.data.results
          this.pagination_data_mismatch.pages = res.data.pages
          this.pagination_data_mismatch.count = res.data.count
        })
    },
    onClickMissingGroupPagination(page) {
      this.pagination_missing_group.page = page
      this.fetchMissingGroupReservations()
    },
    onClickCarsMismatchPagination(page) {
      this.pagination_cars_mismatch.page = page
      this.fetchCarsMismatchReservations()
    },
    onClickDataMismatchPagination(page) {
      this.pagination_data_mismatch.page = page
      this.fetchDataMismatchReservations()
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    problemLabel(kind) {
      return {
        booking_overlap: 'Booking overlap',
        maintenance_conflict: 'Maintenance',
        guide_leave_conflict: 'Assigned guide leave',
      }[kind] || kind
    },
    problemColor(kind) {
      return {
        booking_overlap: '#e85c20',
        maintenance_conflict: '#7840aa',
        guide_leave_conflict: '#cf4747',
      }[kind] || '#404040'
    },
  }
}
</script>

<style scoped>
.schedule-problems__filters { width: fit-content; max-width: 100%; overflow-x: auto; }
.schedule-problems__info { margin-top: 24px; }
.schedule-problems__vehicles { display: grid; gap: 10px; }
.schedule-problems__vehicle { border: 1px solid #dfdfdf; border-radius: 10px; overflow: hidden; background: #fff; box-shadow: 0 1px 2px rgba(0, 0, 0, .08); }
.schedule-problems__vehicle > header { padding: 7px 16px; display: flex; align-items: center; gap: 20px; border-bottom: 1px solid #e8e8e8; background: #fff; }
.schedule-problems__vehicle > header > div { display: flex; align-items: baseline; gap: 8px; }
.schedule-problems__vehicle > header > div > span::before { content: '·'; margin-right: 8px; color: #b0b9be; }
.schedule-problems__vehicle > header > div > strong { color: #26343c; font-size: 18px; }
.schedule-problems__vehicle > header span { color: #7a8790; font-size: 13px; }
.schedule-problems__vehicle > header > b { margin-left: auto; color: #5f5f5f; font-size: 13px; font-weight: 500; white-space: nowrap; }
.schedule-problems__driver { min-width: 280px; padding-left: 20px; border-left: 1px solid #dce3e6; }
.schedule-problems__items { padding: 0 16px; }
.schedule-problems__item { display: grid; grid-template-columns: 170px 220px minmax(0, 1fr); gap: 16px; padding: 10px 0; border-bottom: 1px solid #e5eaed; }
.schedule-problems__item--booking_overlap { grid-template-columns: 170px minmax(0, 1fr); }
.schedule-problems__item--booking_overlap .schedule-problems__bookings { grid-column: 2; }
.schedule-problems__item:last-child { border-bottom: 0; }
.schedule-problems__item-heading, .schedule-problems__availability, .schedule-problems__bookings { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; }
.schedule-problems__dates { display: grid; gap: 2px; }
.schedule-problems__dates span { display: grid; grid-template-columns: 34px 1fr; align-items: baseline; gap: 4px; color: #4c5c66; }
.schedule-problems__dates small, .schedule-problems__booking small { color: #8b969d; font-size: 10px; font-weight: 600; text-transform: uppercase; }
.schedule-problems__dates strong { font-size: 13px; }
.schedule-problems__availability span, .schedule-problems__bookings > span { color: #7a8790; font-size: 13px; }
.schedule-problems__booking { width: 100%; padding: 3px 0; display: grid; grid-template-columns: minmax(150px, 1fr) auto auto; align-items: baseline; gap: 12px; color: #181818; line-height: 1.25; }
.schedule-problems__booking > strong { min-width: 0; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.schedule-problems__booking > span { display: flex; align-items: baseline; gap: 4px; color: #65737b; white-space: nowrap; }
@media (max-width: 900px) {
  .schedule-problems__vehicle > header { align-items: flex-start; flex-wrap: wrap; }
  .schedule-problems__vehicle > header > div { flex-wrap: wrap; }
  .schedule-problems__driver { min-width: 0; padding-left: 0; border-left: 0; }
  .schedule-problems__item, .schedule-problems__item--booking_overlap { grid-template-columns: 1fr; gap: 8px; }
  .schedule-problems__item--booking_overlap .schedule-problems__bookings { grid-column: 1; }
  .schedule-problems__booking { grid-template-columns: 1fr; gap: 2px; }
}
</style>
